import { useEffect, useState } from "react"
import { useRouter } from "next/router"

const SHOW_DELAY_MS = 120
const FINISH_DELAY_MS = 200
const TRICKLE_INTERVAL_MS = 600
const CANCELLATION_DELAY_MS = 300

export function NavigationProgress() {
  const { events } = useRouter()
  const [progress, setProgress] = useState<number | null>(null)

  useEffect(() => {
    let activeRoute: string | undefined
    let showTimer: ReturnType<typeof setTimeout> | undefined
    let finishTimer: ReturnType<typeof setTimeout> | undefined
    let trickleTimer: ReturnType<typeof setInterval> | undefined
    let cancellationTimer: ReturnType<typeof setTimeout> | undefined

    const clearTimers = () => {
      clearTimeout(showTimer)
      clearTimeout(finishTimer)
      clearInterval(trickleTimer)
      clearTimeout(cancellationTimer)
      trickleTimer = undefined
      cancellationTimer = undefined
    }

    const finishProgress = () => {
      clearTimers()
      setProgress((current) => (current === null ? null : 100))
      finishTimer = setTimeout(() => setProgress(null), FINISH_DELAY_MS)
    }

    const start = (url: string, { shallow }: { shallow: boolean }) => {
      if (shallow) {
        return
      }

      const alreadyLoading =
        activeRoute !== undefined || cancellationTimer !== undefined

      clearTimeout(cancellationTimer)
      cancellationTimer = undefined
      activeRoute = url

      if (alreadyLoading) {
        return
      }

      clearTimers()
      setProgress(null)
      showTimer = setTimeout(() => {
        setProgress(12)
        trickleTimer = setInterval(() => {
          setProgress((current) => {
            if (current === null) {
              return current
            }

            return Math.min(90, current + (90 - current) * 0.12)
          })
        }, TRICKLE_INTERVAL_MS)
      }, SHOW_DELAY_MS)
    }

    const finish = (url: string) => {
      if (url !== activeRoute) {
        return
      }

      activeRoute = undefined
      finishProgress()
    }

    const fail = (error: unknown, url: string) => {
      if (url !== activeRoute) {
        return
      }

      const isCancellation =
        typeof error === "object" &&
        error !== null &&
        "cancelled" in error &&
        error.cancelled === true

      if (!isCancellation || trickleTimer === undefined) {
        finish(url)

        return
      }

      activeRoute = undefined
      cancellationTimer = setTimeout(finishProgress, CANCELLATION_DELAY_MS)
    }

    events.on("routeChangeStart", start)
    events.on("routeChangeComplete", finish)
    events.on("routeChangeError", fail)

    return () => {
      clearTimers()
      events.off("routeChangeStart", start)
      events.off("routeChangeComplete", finish)
      events.off("routeChangeError", fail)
    }
  }, [events])

  if (progress === null) {
    return null
  }

  return (
    <div
      role="progressbar"
      aria-label="Loading page"
      className="pointer-events-none fixed inset-x-0 top-0 z-[100000] h-[3px]"
    >
      <div
        className="h-full w-full origin-left bg-blue7 transition-transform duration-200 ease-out motion-reduce:transition-none"
        style={{ transform: `scaleX(${progress / 100})` }}
      />
    </div>
  )
}
